import { createSignal } from "solid-js";
import { Language } from "@monkeytype/schemas/languages";
import Ape from "../ape";
import { Quote } from "../controllers/quotes-controller";
import { showErrorNotification } from "./notifications";
import { showModal } from "./modals";
import { isSafeNumber } from "@monkeytype/util/numbers";

type QuoteStats = {
  average?: number;
  ratings?: number;
  totalRating?: number;
  quoteId?: number;
  language?: Language;
};

const [selectedQuote, setSelectedQuote] = createSignal<Quote | null>(null);
const [quoteStats, setQuoteStats] = createSignal<
  QuoteStats | null | Record<string, never>
>(null);

export { selectedQuote, quoteStats };

export function clearQuoteStats(): void {
  setQuoteStats(null);
}

export function getRatingAverage(stats: QuoteStats): number {
  if (
    isSafeNumber(stats.ratings) &&
    isSafeNumber(stats.totalRating) &&
    stats.ratings > 0 &&
    stats.totalRating > 0
  ) {
    return Math.round((stats.totalRating / stats.ratings) * 10) / 10;
  }
  return 0;
}

export async function getQuoteStats(
  quote?: Quote,
): Promise<QuoteStats | undefined> {
  if (!quote) return undefined;

  setSelectedQuote(quote);
  const response = await Ape.quotes.getRating({
    query: { quoteId: quote.id, language: quote.language },
  });

  // a newer request replaced this one
  if (selectedQuote() !== quote) return undefined;

  if (response.status !== 200) {
    showErrorNotification("Failed to get quote ratings", { response });
    return undefined;
  }

  if (response.body.data === null) {
    setQuoteStats({});
    return {};
  }

  const stats = response.body.data as QuoteStats;
  if (stats !== undefined && stats.average === undefined) {
    stats.average = getRatingAverage(stats);
  }

  setQuoteStats(stats);
  return stats;
}

export function updateQuoteStats(stats: QuoteStats): void {
  setQuoteStats(stats);
}

export function showQuoteRateModal(quote: Quote): void {
  setSelectedQuote(quote);
  showModal("QuoteRate");
}
